import React, { useState, useEffect, useMemo } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Row, Col, Spin } from "antd";
import { PageHeader } from "../../../components/page-headers/page-headers";
import { Main, CardToolbox } from "../../styled";

import { Button } from "../../../components/buttons/buttons";

import { useTranslation } from "react-i18next";
import { AutoComplete } from "../../../components/autoComplete/autoComplete";
import { PanelFiltrov } from "../../../components/filtre/panelFiltrov";

import { GetStorageSearch, GetCiselniky } from "./dataQueries";
import List from "./list";
import { zUrl, doUrl } from "../../../utils/stavVUrl";
import { useZdrzanie, usePoZmene } from "../../../utils/zdrzanie";

const graphqlVariables = {
  tokenGet: process.env.REACT_APP_TOKEN_GET,
  tokenMutation: process.env.REACT_APP_TOKEN_MUTATION,
};

const POPIS_URL = {
  hladanie: "text",
  strana: "cislo",
  naStranu: "cislo",
  branchOfficeId: "cislo",
  carOwnerId: "cislo",
  carRenterId: "cislo",
  season: "text",
  diskType: "text",
  obdobie: "obdobie",
};

const PRAZDNE_FILTRE = {
  branchOfficeId: undefined,
  carOwnerId: undefined,
  carRenterId: undefined,
  season: undefined,
  diskType: undefined,
  obdobie: undefined,
};

const Warehouse = () => {
  const { t } = useTranslation("common");
  const history = useHistory();
  const location = useLocation();
  const zoZaciatku = zUrl(location.search, POPIS_URL);

  // Hladanie, filtre aj stranovanie riesi server, aby bolo vidno vsetky
  // uskladnenky vsetkych pobociek a zaroven sa netahalo 18 000 zaznamov naraz.
  const [searchText, setSearchText] = useState(zoZaciatku.hladanie || "");
  // Rovnake zdrzanie ako v ostatnych zoznamoch, jedno miesto pre vsetky.
  const hladaj = useZdrzanie(searchText, 300);
  const [filtre, setFiltre] = useState({
    ...PRAZDNE_FILTRE,
    branchOfficeId: zoZaciatku.branchOfficeId,
    carOwnerId: zoZaciatku.carOwnerId,
    carRenterId: zoZaciatku.carRenterId,
    season: zoZaciatku.season,
    diskType: zoZaciatku.diskType,
    obdobie: zoZaciatku.obdobie,
  });
  const [pageNumber, setPageNumber] = useState(zoZaciatku.strana || 1);
  const [limit, setLimit] = useState(zoZaciatku.naStranu || 20);

  // Filtre, hladanie a strana ziju v adrese, aby ich krok spat nezahodil.
  useEffect(() => {
    const novy = doUrl(
      {
        ...filtre,
        hladanie: hladaj,
        strana: pageNumber > 1 ? pageNumber : undefined,
        naStranu: limit !== 20 ? limit : undefined,
      },
      POPIS_URL
    );
    if (novy !== location.search) {
      history.replace({ pathname: location.pathname, search: novy });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtre, hladaj, pageNumber, limit]);

  // Az pri skutocnej zmene hladania, nie pri prvom vykresleni, inak by
  // sa strana z adresy hned po otvoreni zoznamu zhodila na jednotku.
  usePoZmene(hladaj, () => {
    setPageNumber(1);
  });

  const ciselniky = GetCiselniky(graphqlVariables, true);
  const pobocky = ciselniky[0]?.data?.getBranchOficces;
  const majitelia = ciselniky[1]?.data?.getCarOwners;
  const najomcovia = ciselniky[2]?.data?.getCarRenters;

  const naMoznosti = (zoznam) =>
    (Array.isArray(zoznam) ? zoznam : [])
      .map((p) => ({ hodnota: Number(p.id), popis: p.company || `${p.firstname || ""} ${p.lastname || ""}`.trim() }))
      .filter((m) => m.popis)
      .sort((a, b) => a.popis.localeCompare(b.popis, "sk"));

  const definicieFiltrov = useMemo(
    () => [
      {
        kluc: "branchOfficeId",
        popis: "Pneuservis",
        typ: "vyber",
        sirka: 4,
        moznosti: naMoznosti(pobocky),
      },
      {
        kluc: "carOwnerId",
        popis: "Majiteľ",
        typ: "vyber",
        sirka: 5,
        moznosti: naMoznosti(majitelia),
      },
      {
        kluc: "carRenterId",
        popis: "Nájomca",
        typ: "vyber",
        sirka: 5,
        moznosti: naMoznosti(najomcovia),
      },
      {
        kluc: "season",
        popis: "Sezóna",
        typ: "vyber",
        sirka: 3,
        moznosti: [
          { hodnota: "ZIMA", popis: "ZIMA" },
          { hodnota: "LETO", popis: "LETO" },
        ],
      },
      {
        kluc: "diskType",
        popis: "Disk",
        typ: "vyber",
        sirka: 3,
        moznosti: [
          { hodnota: "ALU", popis: "ALU" },
          { hodnota: "PLECH", popis: "PLECH" },
          { hodnota: "Bez Disku", popis: "Bez disku" },
        ],
      },
      {
        kluc: "obdobie",
        popis: "Dátum uskladnenia",
        typ: "obdobie",
        sirka: 6,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pobocky, majitelia, najomcovia]
  );

  const obdobie = filtre.obdobie;
  const queryVariables = {
    ...graphqlVariables,
    search: hladaj || undefined,
    limit,
    offset: (pageNumber - 1) * limit,
    branchOfficeId: filtre.branchOfficeId,
    carOwnerId: filtre.carOwnerId,
    carRenterId: filtre.carRenterId,
    season: filtre.season,
    diskType: filtre.diskType,
    dateFrom: obdobie && obdobie[0] ? obdobie[0].format("YYYY-MM-DD") : undefined,
    dateTo: obdobie && obdobie[1] ? obdobie[1].format("YYYY-MM-DD") : undefined,
  };

  const graphQlData = GetStorageSearch(queryVariables, true);
  const odpoved = graphQlData[0]?.data?.getStorageSearch;
  const loading = graphQlData[0]?.loading;
  const error = graphQlData[0]?.error;

  const zaznamy = Array.isArray(odpoved?.records) ? odpoved.records : [];
  const total = odpoved?.total || 0;

  const handleSearch = (text) => {
    setSearchText(typeof text === "string" ? text : "");
  };

  const zmenFilter = (kluc, hodnota) => {
    setFiltre((predosle) => ({ ...predosle, [kluc]: hodnota }));
    setPageNumber(1);
  };

  const vycistiFiltre = () => {
    setFiltre(PRAZDNE_FILTRE);
    setPageNumber(1);
  };

  const handleChangePage = (data) => {
    setPageNumber(data?.pageNumber || 1);
    setLimit(data?.pageSize || 20);
  };

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={t("backend.warehouse.title")}
          subTitle={
            <>
              <span className="title-counter">Hľadať </span>
              <AutoComplete
                onSearch={handleSearch}
                placeholder="EČV, VIN, číslo uskladnenia"
                width="100%"
                patterns
              />
            </>
          }
          buttons={[
            <Button
              className="btn-add_new"
              size="default"
              type="primary"
              key="1"
            >
              <Link to="./warehouse-new">{t("common.addRecord")}</Link>
            </Button>,
          ]}
        />
      </CardToolbox>
      <Main>
        <Row gutter={15}>
          <Col md={24}>
            <PanelFiltrov
              filtre={definicieFiltrov}
              hodnoty={filtre}
              onZmena={zmenFilter}
              onVycistit={vycistiFiltre}
            />
          </Col>
        </Row>
        <Row gutter={15}>
          <Col md={24}>
            {error && (
              <div className="spin">Zoznam uskladnení sa nepodarilo načítať.</div>
            )}
            {loading && zaznamy.length === 0 && (
              <div className="spin">
                <Spin />
              </div>
            )}
            {!error && (!loading || zaznamy.length > 0) && (
              <List
                inputData={zaznamy}
                graphqlVariables={graphqlVariables}
                total={total}
                pageNumber={pageNumber}
                limit={limit}
                search={hladaj}
                filtreDoExportu={{
                  branchOfficeId: filtre.branchOfficeId,
                  carOwnerId: filtre.carOwnerId,
                  carRenterId: filtre.carRenterId,
                  season: filtre.season,
                  diskType: filtre.diskType,
                  dateFrom: queryVariables.dateFrom,
                  dateTo: queryVariables.dateTo,
                }}
                loading={loading}
                sendPageNumberToGrid={handleChangePage}
              />
            )}
          </Col>
        </Row>
      </Main>
    </>
  );
};

export default Warehouse;
